<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      @click.self="handleClose"
    >
      <div class="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <!-- Modal Header -->
        <div class="sticky top-0 bg-white border-b border-dashboard-border p-6 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="p-2 bg-[#22282F]/10 rounded-lg">
              <UserPlus v-if="!userId" class="w-6 h-6 text-[#22282F]" />
              <Edit3 v-else class="w-6 h-6 text-[#22282F]" />
            </div>
            <div>
              <h2 class="text-xl font-bold text-dashboard-text">
                {{ userId ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur' }}
              </h2>
              <p class="text-sm text-dashboard-muted">
                {{ userId ? 'Mettre à jour les informations' : 'Créer un nouveau compte utilisateur' }}
              </p>
            </div>
          </div>
          <button
            @click="handleClose"
            class="p-2 hover:bg-dashboard-bg rounded-lg transition-colors"
          >
            <X class="w-5 h-5 text-dashboard-muted" />
          </button>
        </div>

        <!-- Error/Success Messages -->
        <div v-if="error" class="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p class="font-medium text-red-800">Erreur</p>
            <p class="text-sm text-red-600">{{ error }}</p>
          </div>
        </div>

        <div v-if="success" class="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle2 class="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p class="text-sm text-green-800">{{ success }}</p>
        </div>

        <!-- Modal Content -->
        <form @submit.prevent="handleSubmit" class="p-6 space-y-6">
          <!-- Informations de base -->
          <div>
            <h3 class="text-sm font-semibold text-dashboard-text mb-4 flex items-center gap-2">
              <User class="w-4 h-4" />
              Informations de base
            </h3>
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="block text-sm font-medium text-dashboard-text mb-2">
                  Email <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.email"
                  type="email"
                  required
                  @input="generateUsername"
                  class="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                  placeholder="ex: john.doe@example.com"
                />
              </div>

              <div class="hidden">
                <label class="flex items-center gap-1 text-sm font-medium text-dashboard-text mb-2">
                  Nom d'utilisateur
                  <span class="text-xs text-dashboard-muted italic">(généré automatiquement)</span>
                </label>
                <input
                  v-model="form.username"
                  type="text"
                  readonly
                  class="w-full px-3 py-2 bg-gray-100 border border-dashboard-border rounded-lg text-sm text-dashboard-muted cursor-not-allowed"
                  placeholder="Sera généré depuis l'email"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-dashboard-text mb-2">
                  Prénom <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.first_name"
                  type="text"
                  required
                  class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                  placeholder="ex: John"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-dashboard-text mb-2">
                  Nom <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.last_name"
                  type="text"
                  required
                  class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                  placeholder="ex: Doe"
                />
              </div>

              <template v-if="!userId">
                <div>
                  <label class="block text-sm font-medium text-dashboard-text mb-2">
                    Mot de passe <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.password"
                    type="password"
                    required
                    class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                    placeholder="Entrer le mot de passe"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-dashboard-text mb-2">
                    Confirmer le mot de passe <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.confirm_password"
                    type="password"
                    required
                    class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                    placeholder="Confirmer le mot de passe"
                  />
                </div>
              </template>
            </div>
          </div>

          <!-- Rôle et Organisation -->
          <div>
            <h3 class="text-sm font-semibold text-dashboard-text mb-4 flex items-center gap-2">
              <Briefcase class="w-4 h-4" />
              Rôle et organisation
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-dashboard-text mb-2">
                  Rôle <span class="text-red-500">*</span>
                </label>
                <select
                  v-model="form.profile.role_id"
                  required
                  class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                >
                  <option value="">Sélectionner un rôle</option>
                  <option v-for="role in availableRoles" :key="role.id" :value="role.id">
                    {{ role.nom }} ({{ role.code }})
                  </option>
                </select>
              </div>

              <!-- Direction - Searchable -->
              <div v-if="!isAmbassadeScopedAdmin" class="relative">
                <label class="block text-sm font-medium text-dashboard-text mb-2">
                  Direction
                </label>
                <div class="relative">
                  <input
                    v-model="searchDirection"
                    type="text"
                    @focus="showDirectionDropdown = true"
                    @input="showDirectionDropdown = true"
                    :disabled="isDirectionScopedAdmin"
                    :placeholder="isDirectionScopedAdmin ? 'Direction de votre périmètre' : 'Rechercher une direction...'"
                    class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20 disabled:opacity-70 disabled:cursor-not-allowed"
                  />
                  <Search class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dashboard-muted pointer-events-none" />
                </div>
                
                <!-- Dropdown Direction -->
                <div 
                  v-if="showDirectionDropdown && filteredDirectionsSearch.length > 0"
                  class="absolute z-50 w-full mt-1 bg-white border border-dashboard-border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                >
                  <button
                    v-for="dir in filteredDirectionsSearch"
                    :key="dir.id"
                    type="button"
                    @click="selectDirection(dir)"
                    class="w-full text-left px-3 py-2 hover:bg-[#22282F]/10 transition-colors text-sm border-b border-dashboard-border last:border-b-0"
                  >
                    <div class="font-medium text-dashboard-text">{{ dir.nom }}</div>
                    <div class="text-xs text-dashboard-muted">{{ dir.code }}</div>
                  </button>
                </div>
                
                <!-- Selected Direction Tag -->
                <div v-if="selectedDirection" class="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-[#22282F]/10 text-[#22282F] rounded-lg text-sm">
                  <Briefcase class="w-3 h-3" />
                  <span>{{ selectedDirection.nom }}</span>
                  <button
                    type="button"
                    @click="clearDirection"
                    class="hover:bg-[#22282F]/20 rounded-full p-0.5"
                  >
                    <X class="w-3 h-3" />
                  </button>
                </div>
              </div>

              <!-- Département - Searchable -->
              <div v-if="!isAmbassadeScopedAdmin" class="relative">
                <label class="block text-sm font-medium text-dashboard-text mb-2">
                  Département
                </label>
                <div class="relative">
                  <input
                    v-model="searchDepartement"
                    type="text"
                    @focus="showDepartementDropdown = true"
                    @input="showDepartementDropdown = true"
                    :disabled="!form.profile.direction_id"
                    placeholder="Rechercher un département..."
                    class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <Search class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dashboard-muted pointer-events-none" />
                </div>
                
                <!-- Dropdown Département -->
                <div 
                  v-if="showDepartementDropdown && filteredDepartementsSearch.length > 0"
                  class="absolute z-50 w-full mt-1 bg-white border border-dashboard-border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                >
                  <button
                    v-for="dept in filteredDepartementsSearch"
                    :key="dept.id"
                    type="button"
                    @click="selectDepartement(dept)"
                    class="w-full text-left px-3 py-2 hover:bg-[#22282F]/10 transition-colors text-sm border-b border-dashboard-border last:border-b-0"
                  >
                    <div class="font-medium text-dashboard-text">{{ dept.nom }}</div>
                    <div class="text-xs text-dashboard-muted">{{ dept.code }}</div>
                  </button>
                </div>
                
                <!-- Selected Département Tag -->
                <div v-if="selectedDepartement" class="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-[#22282F]/10 text-[#22282F] rounded-lg text-sm">
                  <Building2 class="w-3 h-3" />
                  <span>{{ selectedDepartement.nom }}</span>
                  <button
                    type="button"
                    @click="clearDepartement"
                    class="hover:bg-[#22282F]/20 rounded-full p-0.5"
                  >
                    <X class="w-3 h-3" />
                  </button>
                </div>
                
                <p v-if="!form.profile.direction_id" class="mt-1 text-xs text-dashboard-muted">
                  Sélectionnez d'abord une direction
                </p>
              </div>

              <!-- Service - Searchable -->
              <div v-if="!isAmbassadeScopedAdmin" class="relative md:col-span-2">
                <label class="block text-sm font-medium text-dashboard-text mb-2">
                  Service
                </label>
                <div class="relative">
                  <input
                    v-model="searchService"
                    type="text"
                    @focus="showServiceDropdown = true"
                    @input="showServiceDropdown = true"
                    :disabled="!form.profile.direction_id"
                    placeholder="Rechercher un service..."
                    class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <Search class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dashboard-muted pointer-events-none" />
                </div>
                
                <!-- Dropdown Service -->
                <div 
                  v-if="showServiceDropdown && filteredServicesSearch.length > 0"
                  class="absolute z-50 w-full mt-1 bg-white border border-dashboard-border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                >
                  <button
                    v-for="service in filteredServicesSearch"
                    :key="service.id"
                    type="button"
                    @click="selectService(service)"
                    class="w-full text-left px-3 py-2 hover:bg-[#22282F]/10 transition-colors text-sm border-b border-dashboard-border last:border-b-0"
                  >
                    <div class="font-medium text-dashboard-text">{{ service.nom }}</div>
                    <div class="text-xs text-dashboard-muted">
                      {{ service.code }}
                      <span v-if="service.departement"> • {{ service.departement.nom }}</span>
                    </div>
                  </button>
                </div>
                
                <!-- Selected Service Tag -->
                <div v-if="selectedService" class="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-[#22282F]/10 text-[#22282F] rounded-lg text-sm">
                  <Building2 class="w-3 h-3" />
                  <span>{{ selectedService.nom }}</span>
                  <button
                    type="button"
                    @click="clearService"
                    class="hover:bg-[#22282F]/20 rounded-full p-0.5"
                  >
                    <X class="w-3 h-3" />
                  </button>
                </div>
                
                <p v-if="!form.profile.direction_id" class="mt-1 text-xs text-dashboard-muted">
                  Sélectionnez d'abord une direction
                </p>
              </div>

              <div>
                <label class="block text-sm font-medium text-dashboard-text mb-2">
                  Matricule
                </label>
                <input
                  v-model="form.profile.matricule"
                  type="text"
                  class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                  placeholder="ex: MAT-2024-001"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-dashboard-text mb-2">
                  Téléphone
                </label>
                <input
                  v-model="form.profile.telephone"
                  type="tel"
                  class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                  placeholder="ex: +229 XX XX XX XX"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-dashboard-text mb-2">
                  Poste
                </label>
                <input
                  v-model="form.profile.poste"
                  type="text"
                  class="w-full px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                  placeholder="ex: Chargé de mission"
                />
              </div>
            </div>
          </div>

          <!-- Permissions backoffice (profil restreint, ex. support) -->
          <div v-if="canManagePermissions && permsCatalog.sections.length">
            <h3 class="text-sm font-semibold text-dashboard-text mb-1 flex items-center gap-2">
              <Settings class="w-4 h-4" />
              Permissions backoffice
            </h3>
            <p class="text-xs text-dashboard-muted mb-4">
              Tout décoché = accès complet selon le rôle. Cochez des sections pour créer
              un profil restreint (ex. support) : il ne verra que ces onglets, et les
              actions non cochées seront désactivées.
            </p>

            <p class="text-xs font-medium text-dashboard-text mb-2">Sections visibles</p>
            <div class="grid grid-cols-2 gap-2 mb-4">
              <label v-for="s in permsCatalog.sections" :key="s.key" class="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  :value="s.key"
                  v-model="form.profile.backoffice_permissions.sections"
                  class="w-4 h-4 text-[#22282F] border-dashboard-border rounded focus:ring-2 focus:ring-[#22282F]/20"
                />
                {{ s.label }}
              </label>
            </div>

            <p class="text-xs font-medium text-dashboard-text mb-2">Actions autorisées (Utilisateurs)</p>
            <div class="grid grid-cols-2 gap-2">
              <label v-for="a in permsCatalog.actions" :key="a.key" class="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  :value="a.key"
                  v-model="form.profile.backoffice_permissions.actions"
                  class="w-4 h-4 text-[#22282F] border-dashboard-border rounded focus:ring-2 focus:ring-[#22282F]/20"
                />
                {{ a.label }}
              </label>
            </div>
          </div>

          <!-- Statut -->
          <div>
            <h3 class="text-sm font-semibold text-dashboard-text mb-4 flex items-center gap-2">
              <Settings class="w-4 h-4" />
              Statut du compte
            </h3>
            <label v-if="userId" class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="form.is_active"
                type="checkbox"
                class="w-5 h-5 text-[#22282F] border-dashboard-border rounded focus:ring-2 focus:ring-[#22282F]/20"
              />
              <div>
                <p class="text-sm font-medium text-dashboard-text">Compte actif</p>
                <p class="text-xs text-dashboard-muted">L'utilisateur peut se connecter et accéder au système</p>
              </div>
            </label>
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-4 border-t border-dashboard-border">
            <button
              type="button"
              @click="handleClose"
              class="flex-1 px-4 py-2.5 bg-dashboard-bg border border-dashboard-border text-dashboard-text rounded-lg text-sm font-medium hover:bg-dashboard-border/50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="flex-1 px-4 py-2.5 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#22282F]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Loader2 v-if="loading" class="w-4 h-4 animate-spin" />
              <Save v-else class="w-4 h-4" />
              {{ userId ? 'Mettre à jour' : 'Créer' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { 
  X, User, UserPlus, Edit3, Briefcase, Settings, 
  AlertCircle, CheckCircle2, Save, Loader2, Search, Building2
} from 'lucide-vue-next'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import { useAuthStore } from '@/store/auth'

const toast = useToast()

interface Props {
  isOpen: boolean
  userId?: number | null
}

interface Emits {
  (e: 'close'): void
  (e: 'success'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const authStore = useAuthStore()

const loading = ref(false)
const error = ref('')
const success = ref('')

// Searchable dropdowns state
const searchDirection = ref('')
const searchDepartement = ref('')
const searchService = ref('')
const showDirectionDropdown = ref(false)
const showDepartementDropdown = ref(false)
const showServiceDropdown = ref(false)
const selectedDirection = ref<any>(null)
const selectedDepartement = ref<any>(null)
const selectedService = ref<any>(null)

const form = ref({
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  confirm_password: '',
  is_active: true,
  profile: {
    role_id: '',
    direction_id: '',
    ambassade_id: '',
    departement_id: '',
    service_id: '',
    matricule: '',
    telephone: '',
    poste: '',
    backoffice_permissions: { sections: [] as string[], actions: [] as string[] }
  }
})

// Permissions backoffice (attribution par un administrateur plein)
const permsCatalog = ref<{ sections: any[]; actions: any[] }>({ sections: [], actions: [] })
const canManagePermissions = computed(() => authStore.isFullBackoffice())

const roles = ref<any[]>([])
const directions = ref<any[]>([])
const departements = ref<any[]>([])
const services = ref<any[]>([])

const currentAdmin = computed(() => authStore.user)
const isAmbassadeScopedAdmin = computed(() => {
  const roleCode = currentAdmin.value?.role?.code
  return roleCode === 'AMBASSADEUR' && !!currentAdmin.value?.ambassade
})
const adminDirection = computed(() => {
  const roleCode = currentAdmin.value?.role?.code
  if (!currentAdmin.value?.direction) return null
  return roleCode === 'ADMIN' || roleCode === 'DIRECTEUR' ? currentAdmin.value.direction : null
})
const isDirectionScopedAdmin = computed(() => Boolean(adminDirection.value))

const availableRoles = computed(() => {
  const roleCode = currentAdmin.value?.role?.code
  if (isAmbassadeScopedAdmin.value) {
    return roles.value.filter(role => role.code === 'AGENT')
  }
  if (!isDirectionScopedAdmin.value) return roles.value

  if (roleCode === 'DIRECTEUR') {
    const allowedCodes = new Set(['CHEF_DEPT', 'CHEF_SERVICE', 'AGENT'])
    return roles.value.filter(role => allowedCodes.has(role.code))
  }

  return roles.value
})

const availableDirections = computed(() => {
  if (!isDirectionScopedAdmin.value || !adminDirection.value) return directions.value
  const scopedDirection = adminDirection.value
  if (!scopedDirection) return directions.value
  return directions.value.filter(direction => direction.id === scopedDirection.id)
})

const filteredDepartements = computed(() => {
  // Si aucune direction sélectionnée, afficher tous les départements
  if (!form.value.profile.direction_id) return departements.value
  
  // Filtrer les départements par direction
  return departements.value.filter(dept => {
    if (!dept.direction) return false
    return dept.direction.id === parseInt(form.value.profile.direction_id)
  })
})

const filteredServices = computed(() => {
  // Si un département est sélectionné, filtrer par département
  if (form.value.profile.departement_id) {
    return services.value.filter(service => {
      if (!service.departement) return false
      return service.departement.id === parseInt(form.value.profile.departement_id)
    })
  }
  
  // Sinon, si une direction est sélectionnée, filtrer par direction
  if (form.value.profile.direction_id) {
    return services.value.filter(service => {
      if (!service.direction) return false
      return service.direction.id === parseInt(form.value.profile.direction_id)
    })
  }
  
  // Sinon afficher tous les services
  return services.value
})

// Computed pour la recherche des directions
const filteredDirectionsSearch = computed(() => {
  if (!searchDirection.value) return availableDirections.value
  
  const query = searchDirection.value.toLowerCase()
  return availableDirections.value.filter(dir =>
    dir.nom.toLowerCase().includes(query) ||
    dir.code.toLowerCase().includes(query)
  )
})

// Computed pour la recherche des départements
const filteredDepartementsSearch = computed(() => {
  let result = filteredDepartements.value
  
  if (searchDepartement.value) {
    const query = searchDepartement.value.toLowerCase()
    result = result.filter(dept =>
      dept.nom.toLowerCase().includes(query) ||
      dept.code.toLowerCase().includes(query)
    )
  }
  
  return result
})

// Computed pour la recherche des services
const filteredServicesSearch = computed(() => {
  let result = filteredServices.value
  
  if (searchService.value) {
    const query = searchService.value.toLowerCase()
    result = result.filter(service =>
      service.nom.toLowerCase().includes(query) ||
      service.code.toLowerCase().includes(query) ||
      (service.departement && service.departement.nom.toLowerCase().includes(query))
    )
  }
  
  return result
})

// Fonctions de sélection
const selectDirection = (dir: any) => {
  if (isDirectionScopedAdmin.value && adminDirection.value && dir.id !== adminDirection.value.id) {
    return
  }
  selectedDirection.value = dir
  form.value.profile.direction_id = String(dir.id)
  searchDirection.value = ''
  showDirectionDropdown.value = false
  
  // Reset département et service
  clearDepartement()
  clearService()
}

const clearDirection = () => {
  if (isDirectionScopedAdmin.value && adminDirection.value) {
    selectedDirection.value = adminDirection.value
    form.value.profile.direction_id = String(adminDirection.value.id)
    searchDirection.value = adminDirection.value.nom
    return
  }
  selectedDirection.value = null
  form.value.profile.direction_id = ''
  searchDirection.value = ''
  
  // Reset département et service
  clearDepartement()
  clearService()
}

const selectDepartement = (dept: any) => {
  selectedDepartement.value = dept
  form.value.profile.departement_id = String(dept.id)
  searchDepartement.value = ''
  showDepartementDropdown.value = false
  
  // Reset service
  clearService()
}

const clearDepartement = () => {
  selectedDepartement.value = null
  form.value.profile.departement_id = ''
  searchDepartement.value = ''
  
  // Reset service
  clearService()
}

const selectService = (service: any) => {
  selectedService.value = service
  form.value.profile.service_id = String(service.id)
  searchService.value = ''
  showServiceDropdown.value = false
}

const clearService = () => {
  selectedService.value = null
  form.value.profile.service_id = ''
  searchService.value = ''
}

const generateUsername = () => {
  if (!props.userId && form.value.email) {
    // Extraire la partie avant le @ de l'email
    const emailParts = form.value.email.split('@')
    if (emailParts.length > 0) {
      form.value.username = emailParts[0].toLowerCase()
    }
  }
}

const fetchRoles = async () => {
  try {
    roles.value = await apiFetch('/api/admin/roles/')
  } catch (e: any) {
    toast.error('Erreur', 'Impossible de charger les rôles')
  }
}

const fetchDirections = async () => {
  try {
    directions.value = await apiFetch('/api/admin/directions/')
    if (adminDirection.value) {
      searchDirection.value = adminDirection.value.nom
      selectedDirection.value = adminDirection.value
      form.value.profile.direction_id = String(adminDirection.value.id)
    }
  } catch (e: any) {
    toast.error('Erreur', 'Impossible de charger les directions')
  }
}

const fetchDepartements = async () => {
  try {
    departements.value = await apiFetch('/api/admin/departements/')
  } catch (e: any) {
    toast.error('Erreur', 'Impossible de charger les départements')
  }
}

const fetchServices = async () => {
  try {
    services.value = await apiFetch('/api/admin/services/')
  } catch (e: any) {
    toast.error('Erreur', 'Impossible de charger les services')
  }
}

const fetchUserData = async () => {
  if (!props.userId) return
  
  try {
    const user = await apiFetch(`/api/admin/users/${props.userId}/`)
    
    form.value = {
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      password: '',
      confirm_password: '',
      is_active: user.is_active,
      profile: {
        role_id: user.profile?.role_id ? String(user.profile.role_id) : '',
        direction_id: user.profile?.direction_id ? String(user.profile.direction_id) : '',
        ambassade_id: user.profile?.ambassade_id ? String(user.profile.ambassade_id) : '',
        departement_id: user.profile?.departement_id ? String(user.profile.departement_id) : '',
        service_id: user.profile?.service_id ? String(user.profile.service_id) : '',
        matricule: user.profile?.matricule || '',
        telephone: user.profile?.telephone || '',
        poste: user.profile?.poste || '',
        backoffice_permissions: {
          sections: [...(user.profile?.backoffice_permissions?.sections || [])],
          actions: [...(user.profile?.backoffice_permissions?.actions || [])]
        }
      }
    }
    
    // Pré-remplir les objets sélectionnés pour l'affichage
    if (user.direction) {
      selectedDirection.value = user.direction
      searchDirection.value = user.direction.nom
    }
    if (user.departement) {
      selectedDepartement.value = user.departement
    }
    if (user.service) {
      selectedService.value = user.service
    }
  } catch (e: any) {
    error.value = e.message || 'Erreur lors du chargement de l\'utilisateur'
    toast.error('Erreur', error.value)
  }
}

const resetForm = () => {
  form.value = {
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    is_active: true,
    profile: {
      role_id: '',
      direction_id: '',
      ambassade_id: '',
      departement_id: '',
      service_id: '',
      matricule: '',
      telephone: '',
      poste: '',
      backoffice_permissions: { sections: [] as string[], actions: [] as string[] }
    }
  }
  error.value = ''
  success.value = ''
  
  // Reset searchable dropdowns
  searchDirection.value = adminDirection.value?.nom || ''
  searchDepartement.value = ''
  searchService.value = ''
  showDirectionDropdown.value = false
  showDepartementDropdown.value = false
  showServiceDropdown.value = false
  selectedDirection.value = null
  selectedDepartement.value = null
  selectedService.value = null

  if (adminDirection.value) {
    selectedDirection.value = adminDirection.value
    form.value.profile.direction_id = String(adminDirection.value.id)
  }
}

const handleClose = () => {
  resetForm()
  emit('close')
}

const handleSubmit = async () => {
  error.value = ''
  success.value = ''
  
  loading.value = true

  try {
    if (!props.userId) {
      if (!form.value.password || !form.value.confirm_password) {
        throw new Error('Le mot de passe et sa confirmation sont obligatoires')
      }
      if (form.value.password !== form.value.confirm_password) {
        throw new Error('Les mots de passe ne correspondent pas')
      }
    }

    // Préparer les données
    const payload: any = {
      email: form.value.email,
      first_name: form.value.first_name,
      last_name: form.value.last_name,
      is_active: props.userId ? form.value.is_active : true,
      profile: {
        role_id: form.value.profile.role_id || null,
        direction_id: form.value.profile.direction_id || null,
        ambassade_id: form.value.profile.ambassade_id || null,
        departement_id: form.value.profile.departement_id || null,
        service_id: form.value.profile.service_id || null,
        matricule: form.value.profile.matricule || '',
        telephone: form.value.profile.telephone || '',
        poste: form.value.profile.poste || ''
      }
    }

    // Permissions backoffice : envoyées seulement par un administrateur plein.
    if (canManagePermissions.value) {
      payload.profile.backoffice_permissions = {
        sections: form.value.profile.backoffice_permissions?.sections || [],
        actions: form.value.profile.backoffice_permissions?.actions || []
      }
    }

    if (isAmbassadeScopedAdmin.value) {
      payload.profile.ambassade_id = currentAdmin.value?.ambassade?.id || null
      payload.profile.direction_id = null
      payload.profile.departement_id = null
      payload.profile.service_id = null
    }

    if (!props.userId) {
      // Création
      payload.username = form.value.username
      payload.password = form.value.password

      if (isAmbassadeScopedAdmin.value) {
        payload.profile.ambassade_id = currentAdmin.value?.ambassade?.id || null
        payload.profile.direction_id = null
        payload.profile.departement_id = null
        payload.profile.service_id = null
      }
      
      await apiFetch('/api/admin/users/create/', {
        method: 'POST',
        body: JSON.stringify(payload)
      })
      
      toast.success('Utilisateur créé', 'L\'utilisateur a été créé avec succès')
    } else {
      // Mise à jour
      await apiFetch(`/api/admin/users/${props.userId}/update/`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      })
      
      toast.success('Utilisateur modifié', 'Les informations ont été mises à jour avec succès')
    }

    setTimeout(() => {
      emit('success')
      handleClose()
    }, 500)

  } catch (e: any) {
    error.value = e.message || 'Une erreur est survenue'
    toast.error('Erreur', error.value)
  } finally {
    loading.value = false
  }
}

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    if (props.userId) {
      fetchUserData()
    } else {
      resetForm()
    }
  }
})

// Fermer les dropdowns quand on clique en dehors
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    showDirectionDropdown.value = false
    showDepartementDropdown.value = false
    showServiceDropdown.value = false
  }
}

const fetchPermsCatalog = async () => {
  try {
    permsCatalog.value = await apiFetch('/api/admin/permissions/catalog/')
  } catch (e) {
    // silencieux : la section Permissions ne s'affichera pas
  }
}

onMounted(() => {
  fetchRoles()
  if (!isAmbassadeScopedAdmin.value) {
    fetchDirections()
    fetchDepartements()
    fetchServices()
  }
  if (canManagePermissions.value) {
    fetchPermsCatalog()
  }

  // Ajouter le listener pour fermer les dropdowns
  document.addEventListener('click', handleClickOutside)
})

// Nettoyer le listener lors de la destruction du composant
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
